import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Button from '../../components/common/Button';
import FormField from '../shared/FormField';
import { documentRepo, lookups } from '../../data/db';

const inputClass =
  'w-full rounded-lg border border-solid border-gray-300 bg-white py-2 px-3 text-sm text-slate-700 focus:border-primary-300 focus:outline-none';

const DocumentEditPage: React.FC = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const users = useMemo(() => lookups.users(), []);

  const existing = useMemo(() => (documentId ? documentRepo.get(documentId) : undefined), [documentId]);

  const [name, setName] = useState(existing?.name ?? '');
  const [type, setType] = useState(existing?.type ?? '');
  const [caseName, setCaseName] = useState(existing?.caseName ?? '');
  const [uploadedById, setUploadedById] = useState(existing?.uploadedBy.id ?? users[0]?.id ?? '');
  const [size, setSize] = useState(existing?.size ?? '');

  if (!documentId || !existing) {
    return (
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6">
        <h1 className="text-2xl font-bold text-slate-700 mb-2">Document not found</h1>
        <Link to="/dashboard/documents">
          <Button variant="primary">Back to Documents</Button>
        </Link>
      </div>
    );
  }

  const canSubmit = name.trim() && type.trim();
  const uploadedBy = users.find((u) => u.id === uploadedById) ?? existing.uploadedBy;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    documentRepo.update(existing.id, {
      name: name.trim(),
      type: type.trim(),
      caseName: caseName.trim() || undefined,
      uploadedBy,
      size: size.trim() || undefined,
    });
    navigate(`/dashboard/documents/${existing.id}`);
  };

  return (
    <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6">
      <p className="text-sm text-slate-500 mb-4">Update document fields.</p>

      <form onSubmit={onSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <FormField label="File name">
            <input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} />
          </FormField>
        </div>
        <FormField label="Type">
          <input className={inputClass} value={type} onChange={(e) => setType(e.target.value)} />
        </FormField>
        <FormField label="Case name" hint="Optional">
          <input className={inputClass} value={caseName} onChange={(e) => setCaseName(e.target.value)} />
        </FormField>
        <FormField label="Uploaded by">
          <select className={inputClass} value={uploadedById} onChange={(e) => setUploadedById(e.target.value)}>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.username}
              </option>
            ))}
          </select>
        </FormField>
        <FormField label="Size" hint="Optional">
          <input className={inputClass} value={size} onChange={(e) => setSize(e.target.value)} />
        </FormField>

        <div className="md:col-span-2 flex items-center justify-end gap-2 mt-2">
          <Button variant="secondary" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={!canSubmit}>
            Save
          </Button>
        </div>
      </form>
    </div>
  );
};

export default DocumentEditPage;

